import type { MainPromptPreset, MainPromptSection } from "../../services/presetAssembler";

export interface MainPresetProfile {
  id: string;
  name: string;
  path: string | null;
  provider: "gemini" | "glm" | "anthropic" | "deepseek" | "generic";
  keyMode?: "official" | "nonofficial";
}

const base = "/presets/mortal-approved/core-chat/";
export const MAIN_PRESET_PROFILES: MainPresetProfile[] = [
  { id: "mh-default", name: "MH 正史互动叙事（默认）", path: null, provider: "generic" },
  { id: "it-takes-two-gemini-v12", name: "Gemini V12.0 · 长夏未央", path: `${base}【Gemini专用】双人成行 V12.0—长夏未央.json`, provider: "gemini" },
  { id: "it-takes-two-glm-v12", name: "GLM V12.0 · 长夏未央", path: `${base}【GLM专用】双人成行 V12.0—长夏未央.json`, provider: "glm" },
  { id: "it-takes-two-claude-v12", name: "Claude Opus 4.6 V12.0 · 长夏未央", path: `${base}【Claude专用】双人成行 V12.0—长夏未央.json`, provider: "anthropic" },
  { id: "it-takes-two-gemini-v61", name: "Gemini V6.1 · 向斜阳（历史版）", path: `${base}【Gemini专用】双人成行 V6.1—向斜阳（历史版）.json`, provider: "gemini" },
  { id: "it-takes-two-ds-official", name: "DeepSeek V4 Pro · 官 Key", path: `${base}【DeepSeek V4 Pro专用】双人成行.json`, provider: "deepseek", keyMode: "official" },
  { id: "it-takes-two-ds-nonofficial", name: "DeepSeek V4 Pro · 非官 Key", path: `${base}【DeepSeek V4 Pro专用】双人成行.json`, provider: "deepseek", keyMode: "nonofficial" },
  { id: "it-takes-two-ds-v61-official", name: "DeepSeek V6.1 · 官 Key（历史版）", path: `${base}【DeepSeek V4 Pro专用】双人成行 V6.1—DS🐳专用（官Key历史版）.json`, provider: "deepseek", keyMode: "official" },
  { id: "it-takes-two-ds-v61-nonofficial", name: "DeepSeek V6.1 · 非官 Key（历史版）", path: `${base}【DeepSeek V4 Pro专用】双人成行 V6.1—DS🐳专用（非官Key历史版）.json`, provider: "deepseek", keyMode: "nonofficial" },
];

interface SillyPrompt {
  identifier?: string;
  name?: string;
  role?: string;
  content?: string;
  enabled?: boolean;
}
interface SillyPreset {
  prompts?: SillyPrompt[];
  prompt_order?: Array<{ order?: Array<{ identifier?: string; enabled?: boolean }> }>;
  assistant_prefill?: string;
}
const cache = new Map<string, Promise<{ preset: MainPromptPreset; assistantPrefill: string }>>();

export function loadApprovedMainPreset(profileId: string) {
  const profile = MAIN_PRESET_PROFILES.find((item) => item.id === profileId);
  if (!profile?.path) return null;
  let pending = cache.get(profile.id);
  if (!pending) {
    pending = fetch(profile.path)
      .then(async (response) => {
        if (!response.ok) throw new Error(`无法载入预设：${profile.name}`);
        return (await response.json()) as SillyPreset;
      })
      .then((raw) => convertSillyPreset(profile, raw));
    cache.set(profile.id, pending);
  }
  return pending;
}

export function convertSillyPreset(profile: MainPresetProfile, raw: SillyPreset) {
  const prompts = new Map((raw.prompts ?? []).map((prompt) => [prompt.identifier, prompt]));
  const ordered = raw.prompt_order?.[0]?.order ?? [];
  const sections: MainPromptSection[] = [];
  for (const entry of ordered) {
    if (!entry.identifier || entry.enabled === false) continue;
    const prompt = prompts.get(entry.identifier);
    if (!prompt || prompt.enabled === false) continue;
    const role = ["system", "user", "assistant"].includes(prompt.role ?? "")
      ? (prompt.role as MainPromptSection["role"])
      : "system";
    sections.push({
      id: entry.identifier === "chatHistory" ? "chatHistory" : entry.identifier,
      name: prompt.name?.trim() || entry.identifier,
      role,
      enabled: true,
      content: prompt.content ?? "",
    });
  }
  if (!sections.some((section) => section.id === "chatHistory"))
    sections.push({
      id: "chatHistory",
      name: "近期对话锚点",
      role: "system",
      enabled: true,
      content: "",
    });
  return {
    preset: { version: 1, id: profile.id, name: profile.name, sections } satisfies MainPromptPreset,
    assistantPrefill: raw.assistant_prefill?.trim() ?? "",
  };
}
